/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — desktop PDF export: self-contained HTML serializer.

   The old "Save as PDF" path rasterized the sheet (html-to-image → PNG →
   jsPDF addImage) — fuzzy, ~13MB, no selectable text. On desktop we now
   let Chromium's OWN print engine draw the REAL sheet DOM instead — vector
   paths, embedded fonts, selectable text — via `webContents.printToPDF`
   (main.ts, `orgchart:export-pdf`). This module builds the standalone
   HTML document main loads into an offscreen window to do that.

   Captures, in order (later wins the cascade):
     1. The sheet element's outerHTML. SVG edge paths serialize as-is —
        they ARE the vector payload. Text content is DOM text, so
        `outerHTML` entity-escapes it automatically (no way for a member
        name to inject a literal `<script>` tag).
     2. Every SAME-ORIGIN stylesheet's CSS text (`document.styleSheets` →
        `cssRules`). Cross-origin sheets (the Google Fonts `<link>` in
        index.html) throw a SecurityError reading `.cssRules` — caught and
        skipped here, re-added as real `<link>` tags in step 3 instead so
        the offscreen window fetches the font files itself and Chromium
        embeds them in the PDF.
     3. Cross-origin stylesheet `<link>` tags, copied verbatim from the
        live document — whatever's actually loaded (no hardcoded URL to
        drift out of sync with index.html if fonts ever change).
     4. A flattened `:root { --token: value; ... }` block built from
        `getComputedStyle(document.documentElement)`, captured while the
        caller has the document forced to `data-theme="light"` (same
        forcing the PNG path already relies on — see PrintExportDialog's
        theme-flip effect). Belt-and-suspenders in case any `var()`
        reference didn't fully resolve from steps 2/3 alone.
   ═══════════════════════════════════════════════════════════════ */

import type { CaptureJob } from './CaptureSheet';

// CSS "in" units are a fixed 96 CSS-px per inch, independent of the
// job's raster-target px/in (up to 300, chosen for PNG quality — vector
// output has no such DPI concept, so the sheet's existing px layout is
// scaled DOWN to the page's true physical size instead).
const CSS_PX_PER_IN = 96;

function collectSameOriginCss(): string {
  const parts: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = sheet.cssRules;
      if (!rules) continue;
      for (const rule of Array.from(rules)) {
        // Skip the app's own @media print rules: printToPDF renders under
        // print media, and base.css ships a global print stylesheet
        // (`body * { visibility: hidden }`, scoped for printing the
        // project dashboard) that would blank the ENTIRE export. This
        // document IS the print layout — app print rules don't apply.
        const media = (rule as CSSMediaRule).media;
        if (media && /print/i.test(media.mediaText)) continue;
        parts.push(rule.cssText);
      }
    } catch {
      // Cross-origin sheet — browser blocks `.cssRules` access. Skipped;
      // re-added as a <link> tag below so the print window fetches it.
    }
  }
  return parts.join('\n');
}

function collectCrossOriginLinkTags(): string {
  const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
  return links
    .filter((link) => {
      const href = (link as HTMLLinkElement).href;
      try {
        return !!href && new URL(href).origin !== window.location.origin;
      } catch {
        return false;
      }
    })
    .map((link) => (link as HTMLLinkElement).outerHTML)
    .join('\n');
}

function collectResolvedRootVars(): string {
  const computed = getComputedStyle(document.documentElement);
  const decls: string[] = [];
  for (let i = 0; i < computed.length; i++) {
    const prop = computed.item(i);
    if (!prop || !prop.startsWith('--')) continue;
    const value = computed.getPropertyValue(prop).trim();
    if (value) decls.push(`${prop}:${value};`);
  }
  return decls.length ? `:root{${decls.join('')}}` : '';
}

/** Builds a self-contained HTML document for `webContents.printToPDF` to
 *  render. The captured sheet subtree keeps its existing raster-target px
 *  size (`job.widthPx` / `job.heightPx`) — it's wrapped in a `transform:
 *  scale()` div that shrinks it uniformly down to the exact physical page
 *  (`widthIn` × `heightIn` inches), mirroring what
 *  `pdf.addImage(dataUrl, 'PNG', 0, 0, wIn, hIn)` does for the raster path
 *  today (same proportions), minus the rasterization step. */
export function buildSheetHtmlDocument(
  sheetEl: HTMLElement,
  job: CaptureJob,
  widthIn: number,
  heightIn: number
): string {
  const scale = (widthIn * CSS_PX_PER_IN) / job.widthPx;
  const css = [collectSameOriginCss(), collectResolvedRootVars()].join('\n');
  const linkTags = collectCrossOriginLinkTags();
  const baseHref = document.baseURI;

  return `<!DOCTYPE html>
<html data-theme="light">
<head>
<meta charset="utf-8">
<base href="${baseHref}">
${linkTags}
<style>${css}</style>
<style>
  @page { size: ${widthIn}in ${heightIn}in; margin: 0; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
</style>
</head>
<body>
<div style="position:relative;width:${widthIn}in;height:${heightIn}in;overflow:hidden;background:#ffffff;">
  <div style="position:absolute;top:0;left:0;transform:scale(${scale});transform-origin:top left;">${sheetEl.outerHTML}</div>
</div>
</body>
</html>`;
}
